import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/env';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { ministriesAPI, officesAPI } from '../../services/api';
import { 
  User, 
  FileText, 
  X,
  AlertCircle,
  CheckCircle,
  Building,
} from 'lucide-react';

interface GrievanceForm {
  isAnonymous: boolean;
  fullName: string;
  email: string;
  phone: string;
  address: string;
  district: string;
  ministryId?: number;
  officeId?: number;
  subject: string;
  description: string;
  category: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  attachments: File[];
  preferredLanguage: 'en' | 'ne';
}

interface Ministry {
  id: number;
  name: string;
  isActive: boolean;
}

interface Office {
  id: number;
  name: string;
  isActive?: boolean;
}

const SubmitGrievancePage: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [grievanceId, setGrievanceId] = useState('');

  const [formData, setFormData] = useState<GrievanceForm>({
    isAnonymous: false,
    fullName: '',
    email: '',
    phone: '',
    address: '',
    district: '',
    ministryId: undefined,
    officeId: undefined,
    subject: '',
    description: '',
    category: '',
    priority: 'MEDIUM',
    attachments: [],
    preferredLanguage: language as 'en' | 'ne'
  });

  const [errors, setErrors] = useState<Partial<GrievanceForm>>({});

  // Fetch ministries and offices
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loadingMinistries, setLoadingMinistries] = useState(true);
  const [loadingOffices, setLoadingOffices] = useState(false);

  useEffect(() => {
    const fetchMinistries = async () => {
      try {
        const response = await ministriesAPI.getAll();
        const data = response.data || [];
        setMinistries(data.filter((m: Ministry) => m.isActive !== false));
      } catch (err) {
        console.error('Error fetching ministries:', err);
      } finally {
        setLoadingMinistries(false);
      }
    };

    fetchMinistries();
  }, []);

  useEffect(() => {
    if (!formData.ministryId) {
      setOffices([]);
      return;
    }

    const fetchOffices = async () => {
      setLoadingOffices(true);
      try {
        const response = await officesAPI.byMinistry(formData.ministryId!);
        const data = response.data || [];
        setOffices(data.filter((o: Office) => o.isActive !== false));
      } catch (err) {
        console.error('Error fetching offices:', err);
        setOffices([]);
      } finally {
        setLoadingOffices(false);
      }
    };

    fetchOffices();
  }, [formData.ministryId]);

  useEffect(() => {
    const element = document.getElementById('grievance-form');

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const categories = [
    { value: 'ADMINISTRATIVE', label: language === 'en' ? 'Administrative Issues' : 'प्रशासनिक मुद्दाहरू' },
    { value: 'INFRASTRUCTURE', label: language === 'en' ? 'Infrastructure Problems' : 'आधारभूत संरचना समस्याहरू' },
    { value: 'HEALTH', label: language === 'en' ? 'Health Services' : 'स्वास्थ्य सेवाहरू' },
    { value: 'EDUCATION', label: language === 'en' ? 'Education' : 'शिक्षा' },
    { value: 'SECURITY', label: language === 'en' ? 'Security & Safety' : 'सुरक्षा र सुरक्षा' },
    { value: 'ENVIRONMENT', label: language === 'en' ? 'Environment' : 'वातावरण' },
    { value: 'OTHER', label: language === 'en' ? 'Other' : 'अन्य' }
  ];

  const districts = [
    'Achham', 'Arghakhanchi', 'Baglung', 'Baitadi', 'Bajhang', 'Bajura', 'Banke', 'Bara', 'Bardiya', 'Bhaktapur',
    'Bhojpur', 'Chitwan', 'Dadeldhura', 'Dailekh', 'Dang', 'Darchula', 'Dhading', 'Dhankuta', 'Dhanusa', 'Dholkha',
    'Dolpa', 'Doti', 'Gorkha', 'Gulmi', 'Humla', 'Ilam', 'Jajarkot', 'Jhapa', 'Jumla', 'Kailali', 'Kalikot', 'Kanchanpur',
    'Kapilvastu', 'Kaski', 'Kathmandu', 'Kavrepalanchok', 'Khotang', 'Lalitpur', 'Lamjung', 'Mahottari', 'Makwanpur',
    'Manang', 'Morang', 'Mugu', 'Mustang', 'Myagdi', 'Nawalparasi (West)', 'Nuwakot', 'Okhaldhunga', 'Palpa', 'Panchthar',
    'Parbat', 'Parsa', 'Pyuthan', 'Ramechhap', 'Rasuwa', 'Rautahat', 'Rolpa', 'Rukum (East)','Rukum (West)', 'Rupandehi', 'Salyan',
    'Sankhuwasabha', 'Saptari', 'Sarlahi', 'Sindhuli', 'Sindhupalchok', 'Siraha', 'Solukhumbu', 'Sunsari', 'Surkhet',
    'Syangja', 'Tanahu', 'Taplejung', 'Terhathum', 'Udayapur', 'Nawalparasi (East)'
  ];

  const handleInputChange = (field: keyof GrievanceForm, value: string | number | undefined | File[] | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleMinistryChange = (value: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      ministryId: value,
      officeId: undefined,
    }));
    if (errors.ministryId) {
      setErrors(prev => ({ ...prev, ministryId: undefined }));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles: File[] = [];

    files.forEach(file => {
      if (file.size <= 2 * 1024 * 1024) {
        validFiles.push(file);
      } else {
        alert(language === 'en' ? `${file.name} exceeds 2 MB` : `${file.name} २ MB भन्दा ठूलो छ`);
      }
    });

    const totalFiles = [...formData.attachments, ...validFiles].slice(0, 3);
    setFormData(prev => ({ ...prev, attachments: totalFiles }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<GrievanceForm> = {};

    if (!formData.isAnonymous && !formData.fullName.trim()) {
      newErrors.fullName = language === 'en' ? 'Full name is required for non-anonymous submissions' : 'गैर-गोपनीय पेसको लागि पूरा नाम आवश्यक छ';
    }
    if (!formData.description.trim()) newErrors.description = language === 'en' ? 'Description is required' : 'विवरण आवश्यक छ';
    if (!formData.category) newErrors.category = language === 'en' ? 'Category is required' : 'श्रेणी आवश्यक छ';

    setErrors(newErrors);

    // Scroll to first error
    const firstErrorField = Object.keys(newErrors)[0];
    if (firstErrorField) {
      const el = document.querySelector(`[name="${firstErrorField}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmitClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    // Show confirmation modal instead of submitting directly
    setShowConfirmModal(true);
  };

  const executeSubmission = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    try {
      // Create FormData
      const form = new FormData();

      form.append('isAnonymous', formData.isAnonymous.toString());
      form.append('fullName', formData.fullName);
      form.append('email', formData.email);
      form.append('phone', formData.phone);
      form.append('address', formData.address);
      form.append('district', formData.district);
      if (formData.ministryId) form.append('ministry_id', formData.ministryId.toString());
      if (formData.officeId) form.append('office_id', formData.officeId.toString());
      form.append('subject', formData.subject);
      form.append('description', formData.description);
      form.append('category', formData.category);
      form.append('priority', formData.priority);
      form.append('preferredLanguage', formData.preferredLanguage);

      // Append files properly
      formData.attachments.forEach(file => {
        form.append('attachments', file);
      });

      // Fetch POST request
      const response = await fetch(`${API_URL}/grievances`, {
        method: 'POST',
        body: form,
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || 'Failed to submit grievance');
      window.scrollTo({
        top: 0,
        behavior: "smooth", // use "auto" for instant scrolling
      });
      setGrievanceId(data.grievanceId);
      setShowSuccess(true);

    } catch (err: any) {
      console.error('Error submitting grievance:', err);
      alert(err.message || 'Failed to submit grievance. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Grievance Submitted Successfully!' : 'गुनासो सफलतापूर्वक पेश गरियो!'}
            </h1>
            <p className="text-gray-600 mb-6">
              {language === 'en' 
                ? 'Your grievance has been submitted and is being processed.'
                : 'तपाईंको गुनासो पेश गरिएको छ र प्रक्रियामा छ।'
              }
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">
                {language === 'en' ? 'Grievance ID:' : 'गुनासो आईडी:'}
              </p>
              <p className="text-lg font-mono font-bold text-primary-600">{grievanceId}</p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              {language === 'en' 
                ? 'Please save this ID to track your grievance status.'
                : 'कृपया आफ्नो गनासो स्थिति ट्र्याक गर्न यो आईडी सेभ गर्नुहोस्।'
              }
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate(`/track/${grievanceId}`)}
                className="btn-primary"
              >
                {language === 'en' ? 'Track This Grievance' : 'यो गुनासो ट्र्याक गर्नुहोस्'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="btn-outline"
              >
                {language === 'en' ? 'Back to Home' : 'गृहपृष्ठमा फिर्ता'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="grievance-form" className="min-h-screen bg-gray-50 py-8 relative">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {language === 'en' ? 'Submit Grievance' : 'गुनासो पेश गर्नुहोस्'}
            </h1>
            <p className="text-gray-600">
              {language === 'en' 
                ? 'Please fill out the form below to submit your grievance. All fields marked with * are required.'
                : 'कृपया आफ्नो गुनासो पेश गर्न तलको फारम भर्नुहोस्। * चिह्न लगाइएका सबै फिल्डहरू आवश्यक छन्।'
              }
            </p>
          </div>

          <form onSubmit={handleFormSubmitClick} className="space-y-8">
            {/* Personal Information */}
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  {language === 'en' ? 'Personal Information' : 'व्यक्तिगत जानकारी'}
                </h2>
                
                {/* Anonymous Toggle Checkbox */}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isAnonymous}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({ 
                        ...prev, 
                        isAnonymous: checked,
                        fullName: checked ? '' : prev.fullName 
                      }));
                      if (errors.fullName) {
                        setErrors(prev => ({ ...prev, fullName: undefined }));
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {language === 'en' ? 'Submit Anonymously' : 'गोपनीय रूपमा पेश गर्नुहोस्'}
                  </span>
                </label>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="label">
                    {language === 'en' ? 'Full Name' : 'पूरा नाम'}
                    {!formData.isAnonymous && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    name="fullName"
                    type="text"
                    disabled={formData.isAnonymous}
                    className={`input ${formData.isAnonymous ? 'bg-gray-100 cursor-not-allowed' : ''} ${errors.fullName ? 'input-error' : ''}`}
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    placeholder={
                      formData.isAnonymous 
                        ? (language === 'en' ? 'Disabled (Anonymous)' : 'अक्षम गरिएको (गोपनीय)')
                        : (language === 'en' ? 'Enter your full name' : 'आफ्नो पूरा नाम प्रविष्ट गर्नुहोस्')
                    }
                    aria-invalid={!!errors.fullName}
                  />
                  {errors.fullName && <p className="form-error flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.fullName}</p>}
                </div>

                <div className="form-group">
                  <label className="label">{language === 'en' ? 'Email Address' : 'इमेल ठेगाना'}</label>
                  <input
                    name="email"
                    type="email"
                    className={`input ${errors.email ? 'input-error' : ''}`}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder={language === 'en' ? 'Enter your email' : 'आफ्नो इमेल प्रविष्ट गर्नुहोस्'}
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && <p className="form-error flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.email}</p>}
                </div>

                <div className="form-group">
                  <label className="label">{language === 'en' ? 'Phone Number' : 'फोन नम्बर'}</label>
                  <input
                    name="phone"
                    type="tel"
                    className={`input ${errors.phone ? 'input-error' : ''}`}
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder={language === 'en' ? 'Enter your phone number' : 'आफ्नो फोन नम्बर प्रविष्ट गर्नुहोस्'}
                    aria-invalid={!!errors.phone}
                  />
                  {errors.phone && <p className="form-error flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.phone}</p>}
                </div>

                <div className="form-group">
                  <label className="label">{language === 'en' ? 'District' : 'जिल्ला'}</label>
                  <select
                    className="input"
                    value={formData.district}
                    onChange={(e) => handleInputChange('district', e.target.value)}
                  >
                    <option value="">{language === 'en' ? 'Select District' : 'जिल्ला छान्नुहोस्'}</option>
                    {districts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                <div className="form-group md:col-span-2">
                  <label className="label">{language === 'en' ? 'Address' : 'ठेगाना'}</label>
                  <textarea
                    name="address"
                    className={`input ${errors.address ? 'input-error' : ''}`}
                    rows={3}
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={language === 'en' ? 'Enter your complete address' : 'आफ्नो पूरा ठेगाना प्रविष्ट गर्नुहोस्'}
                    aria-invalid={!!errors.address}
                  />
                  {errors.address && <p className="form-error flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.address}</p>}
                </div>
              </div>
            </div>

            {/* Related Ministry/Office */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Building className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Related Ministry/Office' : 'सम्बन्धित मन्त्रालय/कार्यालय'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="label">{language === 'en' ? 'Ministry' : 'गुनासो गर्ने मन्त्रालय वा कार्यालय वा अन्तर्गतका निकायहरु'}</label>
                  <select
                    className="input"
                    value={formData.ministryId || ''}
                    onChange={(e) => handleMinistryChange(e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={loadingMinistries}
                  >
                    <option value="">{language === 'en' ? 'Select Ministry' : 'मन्त्रालय वा कार्यालय वा अन्तर्गतका निकायहरु छान्नुहोस्'}</option>
                    {ministries.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="label">{language === 'en' ? 'Office' : 'कार्यालय'}</label>
                  <select
                    className="input"
                    value={formData.officeId || ''}
                    onChange={(e) => handleInputChange('officeId', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={!formData.ministryId || loadingOffices}
                  >
                    <option value="">
                      {loadingOffices
                        ? (language === 'en' ? 'Loading offices…' : 'कार्यालयहरू लोड हुँदै…')
                        : (language === 'en' ? 'Select Office' : 'कार्यालय छान्नुहोस्')}
                    </option>
                    {offices.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Grievance Details */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Grievance Details' : 'गुनासो विवरण'}
              </h2>

              <div className="space-y-6">
                <div className="form-group">
                  <label className="label">{language === 'en' ? 'Subject' : 'विषय'}</label>
                  <input
                    name="subject"
                    type="text"
                    className={`input ${errors.subject ? 'input-error' : ''}`}
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder={language === 'en' ? 'Brief description of your grievance' : 'आफ्नो गुनासोको संक्षिप्त विवरण'}
                    aria-invalid={!!errors.subject}
                  />
                  {errors.subject && <p className="form-error flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.subject}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <label className="label">{language === 'en' ? 'Types' : 'प्रकार'} *</label>
                    <select
                      name="category"
                      className={`input ${errors.category ? 'input-error' : ''}`}
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      aria-invalid={!!errors.category}
                    >
                      <option value="">{language === 'en' ? 'Types Category' : 'प्रकार छान्नुहोस्'}</option>
                      {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    {errors.category && <p className="form-error flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.category}</p>}
                  </div>

                  <div className="form-group">
                    <label className="label">{language === 'en' ? 'Priority' : 'प्राथमिकता'}</label>
                    <select
                      className="input"
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value as any)}
                    >
                      <option value="LOW">{language === 'en' ? 'Low' : 'कम'}</option>
                      <option value="MEDIUM">{language === 'en' ? 'Medium' : 'मध्यम'}</option>
                      <option value="HIGH">{language === 'en' ? 'High' : 'उच्च'}</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="label">{language === 'en' ? 'Description' : 'विवरण'} *</label>
                  <textarea
                    name="description"
                    className={`input ${errors.description ? 'input-error' : ''}`}
                    rows={5}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={language === 'en' ? 'Describe your grievance in detail' : 'आफ्नो गुनासो विस्तारमा वर्णन गर्नुहोस्'}
                    aria-invalid={!!errors.description}
                  />
                  {errors.description && <p className="form-error flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.description}</p>}
                </div>

                <div className="form-group">
                  <label className="label">{language === 'en' ? 'Attachments (max 3,  2 MB each)' : 'गुनासो सम्बन्धी कुनै फाइल भए अपलोड गर्नुहोस् (अधिकतम ३, प्रत्येक २ MB)'}</label>
                  <input
                    name="attachments"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="input"
                  />
                  {formData.attachments.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Selected Files:' : 'छानिएका फाइलहरू:'}
                      </p>
                      <div className="space-y-2">
                        {formData.attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <span className="text-sm text-gray-600">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const newFiles = formData.attachments.filter((_, i) => i !== index);
                                setFormData(prev => ({ ...prev, attachments: newFiles }));
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
              >
                {language === 'en' ? 'Submit Grievance' : 'गुनासो पेश गर्नुहोस्'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal Pop-up */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center animate-fadeIn">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {language === 'en' ? 'Confirm Submission' : 'पेश गर्न पुष्टि गर्नुहोस्'}
            </h3>
            <p className="text-gray-600 mb-6">
              {language === 'en'
                ? 'Are you sure you want to submit this grievance? Once submitted, you cannot edit it.'
                : 'के तपाईं यो गुनासो पेश गर्न निश्चित हुनुहुन्छ? एक पटक पेश गरेपछि, तपाईं यसलाई सम्पादन गर्न सक्नुहुन्न।'}
            </p>
            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                {language === 'en' ? 'Cancel' : 'रद्द गर्नुहोस्'}
              </button>
              <button
                type="button"
                onClick={executeSubmission}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition"
              >
                {language === 'en' ? 'Okay / Submit' : 'ठिक छ / पेश गर्नुहोस्'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitGrievancePage;